import React from "react";
import { Link } from "react-router-dom";
import { fmtINR } from "@/lib/api";
import { ShoppingCart, PhoneCall, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product, onCallback }) {
  const { addItem } = useCart();
  const hasPrice = product.price > 0;

  return (
    <div
      className="group flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden tile-hover"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/products/${product.slug}`} className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {product.highlight && (
          <span className="absolute top-3 left-3 bg-[#FF6D00] text-white text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full">
            {product.highlight}
          </span>
        )}
      </Link>

      <div className="flex-1 flex flex-col p-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
          {product.category}
        </div>
        <Link to={`/products/${product.slug}`}>
          <h3 className="font-heading text-lg font-semibold text-[#0A192F] leading-snug hover:text-[#FF6D00] transition-colors">
            {product.name}
          </h3>
        </Link>
        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{product.short_description}</p>

        <ul className="mt-3 space-y-1 text-xs text-slate-600">
          {product.features.slice(0, 3).map((f) => (
            <li key={f} className="flex items-start gap-2">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FF6D00] flex-shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex items-end justify-between">
          <div>
            {hasPrice ? (
              <>
                <div className="font-heading text-2xl font-bold text-[#0A192F]">{fmtINR(product.price)}</div>
                {product.mrp && product.mrp > product.price && (
                  <div className="text-xs text-slate-500 line-through">{fmtINR(product.mrp)}</div>
                )}
              </>
            ) : (
              <div className="font-heading text-lg font-semibold text-[#FF6D00]">Request a Quote</div>
            )}
          </div>
          {product.monthly_subscription > 0 && (
            <div className="text-right text-xs text-slate-500">
              <div>+ {fmtINR(product.monthly_subscription)}/mo</div>
              <div>subscription</div>
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-col gap-2">
          {hasPrice ? (
            <button
              onClick={() => { addItem(product); toast.success("Added to cart"); }}
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-[#0A192F] text-white text-sm font-semibold hover:bg-[#112240] active:scale-95 transition-transform"
              data-testid={`add-to-cart-${product.slug}`}
            >
              <ShoppingCart className="w-4 h-4" /> Add to Cart
            </button>
          ) : (
            <Link
              to={`/products/${product.slug}`}
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-[#FF6D00] text-white text-sm font-semibold hover:bg-[#ff8433] transition-colors"
              data-testid={`request-quote-${product.slug}`}
            >
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Link
              to={`/products/${product.slug}`}
              className="inline-flex items-center justify-center h-10 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:border-[#0A192F] transition-colors"
              data-testid={`view-details-${product.slug}`}
            >
              Details
            </Link>
            <button
              onClick={() => onCallback && onCallback(product)}
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:border-[#FF6D00] hover:text-[#FF6D00] transition-colors"
              data-testid={`callback-${product.slug}`}
            >
              <PhoneCall className="w-3.5 h-3.5" /> Callback
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
